import type { BadgeTone } from '@zarya/ui';
import { t } from '@app/i18n';
import type { components } from '@app/api/types';

export type Decision = components['schemas']['Decision'];
export type Verdict = Decision['verdict'];
export type DecisionKind = Decision['kind'];

/**
 * O que o PDP decidiu (DEF-34, 04/09): uma tool, uma delegação A2A ou um método
 * de sessão. O campo é obrigatório no contrato; a derivação abaixo existe só
 * para uma página servida por um backend anterior ao campo — a mesma conta que
 * o backend faz para um executor antigo (`agent` → `a2a`; tool vazia → `session`).
 */
export function decisionKind(decision: Decision): DecisionKind {
  if (decision.kind) return decision.kind;
  if (decision.agent) return 'a2a';
  return decision.tool ? 'tool' : 'session';
}

/**
 * O que a chamada citou — a coluna «Destino» de Decisões (T16) e de Visão geral (T3).
 *
 * Numa delegação A2A a chamada não cita tool: cita um **agente**, e é ele que a
 * coluna mostra, com o chip «A2A». Antes de DEF-34 a linha saía com Tool e
 * Método em branco, porque só `mcp_name` chegava até aqui.
 *
 * Phase 2 (approved prototype, `docs/prototype/recurso-agente-aprovado/Decisoes.dc.html`): a tool is the pair
 * `server/tool`; a delegation is the agent resource the PDP returned by id, named as it is now, with `external` for
 * the external agent. A delegation without the resource shows the path it carries: never resolved to a resource
 * that may not be the same; with an id whose resource was removed, it says so (`removed`). The marker of the records
 * from before phase 2 (`legacy`) left with the old path (aim-cpj).
 */
export type DecisionTargetView = {
  texto: string;
  a2a: boolean;
  external: boolean;
  removed: boolean;
};

export function decisionTarget(decision: Decision): DecisionTargetView {
  const view = { a2a: false, external: false, removed: false };
  if (decisionKind(decision) === 'a2a') {
    if (decision.target) {
      return { ...view, a2a: true, texto: decision.target.name, external: decision.target.scope === 'external' };
    }
    const path = decision.agent ?? '';
    return { ...view, a2a: true, texto: path, removed: Boolean(decision.target_resource_id) };
  }
  if (decision.server && decision.tool) return { ...view, texto: `${decision.server}/${decision.tool}` };
  // aim-uig.40 §6: a chamada fora do padrão a uma API sem operação (um MCP nativo fora de mcp/) chega com
  // `tool` vazio e o `server` preenchido. O destino é o servidor; sem isto a célula ficava vazia.
  if (decision.server) return { ...view, texto: decision.server };
  return { ...view, texto: decision.tool };
}

/**
 * A coluna «Método»: o `rpc_method` como veio. Numa delegação de antes da fase 2 ele não vem, e a coluna diz `a2a`;
 * desde a fase 2 a decisão traz o método A2A (`SendMessage`, …), e é ele que se mostra.
 */
export function decisionMethod(decision: Decision): string | null {
  if (decisionKind(decision) === 'a2a') return a2aMethod(decision.rpc_method);
  return decision.rpc_method ?? null;
}

/**
 * The method of a delegation, in the list and in the drawer: the A2A method it carries, or `a2a` when it carries none.
 * An MCP method on a delegation is a record of the KQL defect fixed on 03/10 (it read the first `rpc_method` of the
 * line, the diagnostic's `tools/call`): it is not the delegation's method, and the screen says `a2a`.
 */
export function a2aMethod(rpcMethod: string | null | undefined): string {
  if (!rpcMethod || rpcMethod.startsWith('tools/')) return 'a2a';
  return rpcMethod;
}

/**
 * DEF-29 (09/09): a frase antiga — «decidida pelo contrato do chamador, sem eixos
 * de tool e pessoa» — descrevia o desenho anterior, em que a delegação era barrada
 * antes dos eixos. Hoje ela é capability e passa pelos mesmos dois. Uma ressalva
 * que descreve comportamento que mudou é pior que nenhuma: ela ensina errado.
 */
export function a2aTip(): string {
  return t('decisions.a2a.tip');
}

/**
 * Vocabulário das decisões (T3, T16, T17) — §4.5 do inventário e FR-097.
 *
 * O contrato fala `permitido | negado | indeterminado`; a tela mostra
 * `ALLOW | DENY | indeterminado`, como o protótipo. `indeterminado` é um veredito
 * de verdade, não ausência de dado: o log existe e não diz o que aconteceu.
 */
const VERDICT_TOM: Record<Verdict, BadgeTone> = {
  permitido: 'success',
  negado: 'danger',
  indeterminado: 'neutral',
};

/**
 * Função, e não `Record` de módulo: o rótulo sai do catálogo e uma tabela é
 * avaliada na importação — com o seletor de idioma, ela repintaria no idioma
 * anterior sem nada ficar vermelho. O tom continua tabela: cor não tem idioma.
 */
export function verdict(valor: Verdict): { rotulo: string; tone: BadgeTone } {
  return { rotulo: t(`decisions.verdict.${valor}`), tone: VERDICT_TOM[valor] };
}

/**
 * Quatro degradações declaradas no contrato. A tela mostra cada uma em vez de
 * valor falso — é a diferença entre "não sei" e "não tem".
 */

/**
 * Aplicação nula = o SPIFFE ID chamou e o produto não conhece a identidade.
 * Mostrar "—" apagaria justamente a informação que o operador precisa: **quem**
 * bateu na porta sem estar cadastrado.
 */
export function decisionIdentity(decision: Decision): { texto: string; conhecida: boolean } {
  if (decision.application) return { texto: decision.application, conhecida: true };
  return { texto: decision.spiffe_id, conhecida: false };
}

/** Sujeito nulo = chamada sem pessoa por trás (máquina a máquina). */
export function decisionPerson(decision: Decision): { texto: string; anonima: boolean } {
  if (!decision.sub) return { texto: t('decisions.noPerson'), anonima: true };
  const perfil = decision.profile ? ` · ${decision.profile}` : '';
  return { texto: `${decision.sub}${perfil}`, anonima: false };
}

/** Veredito inferido do status do gateway, não lido do PDP → chip "inferido". */
export function verdictInferred(decision: Decision): boolean {
  return decision.verdict_source === 'gateway_status';
}

export function inferredTip(): string {
  return t('decisions.inferred.tip');
}

/**
 * A decisão saiu do Simulador (T18) ou da lente «Ver como» do mapa (T19).
 *
 * Ela é do PDP de verdade — as duas telas perguntam ao mesmo PDP, com o mesmo
 * Rego, e é isso que faz a resposta valer. O que **não** houve foi a chamada:
 * ninguém pediu aquela tool e ninguém tomou aquele 403. Por isso a linha fica
 * fora da lista por padrão, e por isso, quando entra, vem marcada.
 */
export function simulatedTip(): string {
  return t('decisions.simulated.tip');
}

/**
 * A decisão é uma chamada de controle do executor (D1, aim-6ph.8): a descoberta dele pelo gateway. Só o `true` conta:
 * a linha que não diz (guardada antes do aim-6ph.8) não é chamada de controle.
 */
export function isControlCall(decision: Pick<Decision, 'control_call'>): boolean {
  return decision.control_call === true;
}

/** A dica do selo «controle»: o que a linha é, e por que ela fica fora da lista por padrão. */
export function controlCallTip(): string {
  return t('decisions.controlCall.tip');
}

/**
 * `source` da página — QUEM SERVIU esta lista (Lote 2b, P9).
 *
 * ## O rótulo deixou de sair da fonte, e é o ponto
 *
 * Até aqui este campo trazia o que o executor dizia sobre si, e o valor
 * configurado — «log analytics» — chegava à tela **como veio**. Duas coisas
 * ruins saíam disso: a tela falava o nome de um produto de terceiro que o
 * cliente pode nem usar, e o rótulo mandava o operador conferir o Container
 * Insights (desligado no lab) enquanto o dado vinha do decision log do OPA
 * (DEF-04).
 *
 * Agora o valor é de VOCABULÁRIO FECHADO — `store` ou `executor` — e o texto é
 * do catálogo. Um valor fora do vocabulário cai no genérico em vez de ser
 * impresso cru: um executor mais novo que esta tela não pode escrever na
 * interface.
 */
export function sourceLabel(source: string | null | undefined): string {
  if (source === 'store') return t('decisions.source.store');
  if (source === 'executor') return t('decisions.source.executor');
  return t('decisions.source.unknown');
}

/**
 * De onde o EXECUTOR leu (`executor_source`), quando isso é dizível na tela.
 *
 * `null` em três casos, e os três são deliberados: página servida pelo histórico
 * (que não tem essa opinião — as linhas podem ter vindo das duas fontes); campo
 * ausente; e **valor fora do vocabulário**.
 *
 * O terceiro é o que fecha o DEF-04 pelo outro lado. O valor configurado da
 * outra fonte é texto livre — «log analytics» por padrão —, e imprimi-lo punha
 * na interface o nome de um produto de terceiro que o cliente pode nem usar, e
 * mandava o operador conferir um Container Insights que no lab está desligado.
 * O que a tela sabe nomear, ela nomeia; o resto ela cala, e quem precisa do
 * diagnóstico o lê no campo da API.
 */
export function executorSourceLabel(source: string | null | undefined): string | null {
  if (source === 'collector') return t('decisions.source.collector');
  return null;
}

/** `41.5` → `42 ms`; `null` → `—`. */
export function duration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  return `${Math.round(ms)} ms`;
}
